import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Bot, X, Send, ChevronDown, Check, Sparkles, Mic, MicOff } from 'lucide-react';
import { api } from '../api';

const SUGGESTIONS = [
  'Qui est absent aujourd\'hui ?',
  'Combien d\'employés actifs ?',
  'Qui a badgé ce matin ?',
  'Clients actifs en ce moment ?',
];

const ACTION_LABELS = {
  create_absence: (a) => `Créer une absence (${a.type_absence}) pour ${a.employe_nom} du ${a.date_debut} au ${a.date_fin}`,
  navigate:       (a) => `Aller sur ${a.page}`,
  send_push:      (a) => `Notification à ${a.destinataire_nom} — "${a.titre}"`,
  send_email:     (a) => `Email à ${a.destinataire_nom} — "${a.sujet}"`,
  send_sms:       (a) => `SMS à ${a.destinataire_nom} (${a.destinataire_telephone})`,
};

const ACTION_PREVIEW = {
  send_push:  (a) => a.message,
  send_email: (a) => a.message,
  send_sms:   (a) => a.message,
};

export default function AssistantIA({ currentPage, onNavigate }) {
  const [open, setOpen]         = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput]       = useState('');
  const [loading, setLoading]   = useState(false);
  const [dismissed, setDismissed] = useState(new Set());
  const [listening, setListening] = useState(false);
  const bottomRef  = useRef(null);
  const inputRef   = useRef(null);
  const recognRef  = useRef(null);

  const startListening = useCallback(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    if (listening) { recognRef.current?.stop(); return; }
    const r = new SR();
    r.lang = 'fr-FR';
    r.interimResults = false;
    r.maxAlternatives = 1;
    r.onstart  = () => setListening(true);
    r.onend    = () => setListening(false);
    r.onerror  = () => setListening(false);
    r.onresult = (e) => {
      const transcript = e.results[0][0].transcript;
      setInput(transcript);
      setTimeout(() => send(transcript), 100);
    };
    recognRef.current = r;
    r.start();
  }, [listening]);

  useEffect(() => {
    if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || loading) return;
    setInput('');
    const userMsg = { role: 'user', content: msg };
    setMessages(prev => [...prev, userMsg]);
    setLoading(true);
    try {
      const { reply, action } = await api.assistant({
        message: msg,
        history: messages,
        page: currentPage,
      });
      setMessages(prev => [...prev, { role: 'assistant', content: reply, action }]);
    } catch (err) {
      setMessages(prev => [...prev, { role: 'assistant', content: `Erreur : ${err.message}` }]);
    }
    setLoading(false);
  };

  const executeAction = async (action, idx) => {
    setDismissed(prev => new Set([...prev, idx]));
    try {
      if (action.type === 'send_push') {
        await api.envoyerPush({
          destinataire_nom:   action.destinataire_nom,
          destinataire_type:  action.destinataire_type,
          destinataire_id:    action.destinataire_id,
          titre:   action.titre,
          message: action.message,
        });
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: `✓ Notification envoyée à ${action.destinataire_nom}.`,
        }]);
      } else if (action.type === 'create_absence') {
        await api.addAbsence(action.employe_id, {
          date_debut:  action.date_debut,
          date_fin:    action.date_fin,
          type:        action.type_absence,
          commentaire: action.commentaire || '',
        });
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: `✓ Absence créée pour ${action.employe_nom} du ${action.date_debut} au ${action.date_fin}.`,
        }]);
      } else if (action.type === 'navigate' && onNavigate) {
        onNavigate(action.page);
        setOpen(false);
      } else if (action.type === 'send_email') {
        await api.envoyerEmail({
          destinataire_nom:   action.destinataire_nom,
          destinataire_email: action.destinataire_email,
          sujet:   action.sujet,
          message: action.message,
        });
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: `✓ Email envoyé à ${action.destinataire_nom} (${action.destinataire_email}).`,
        }]);
      } else if (action.type === 'send_sms') {
        await api.envoyerSms({
          destinataire_nom:       action.destinataire_nom,
          destinataire_telephone: action.destinataire_telephone,
          message: action.message,
        });
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: `✓ SMS envoyé à ${action.destinataire_nom}.`,
        }]);
      }
    } catch (err) {
      setMessages(prev => [...prev, { role: 'assistant', content: `Erreur : ${err.message}` }]);
    }
  };

  return (
    <>
      {/* Styles animation */}
      <style>{`
        @keyframes qbdp-bounce { 0%,80%,100%{transform:scale(0)} 40%{transform:scale(1)} }
        @keyframes qbdp-pop { from{opacity:0;transform:scale(.9) translateY(8px)} to{opacity:1;transform:scale(1) translateY(0)} }
        @keyframes qbdp-pulse { 0%,100%{box-shadow:0 0 0 0 rgba(239,68,68,0.5)} 50%{box-shadow:0 0 0 6px rgba(239,68,68,0)} }
      `}</style>

      {/* Bulle flottante */}
      <button
        onClick={() => setOpen(o => !o)}
        title="Assistant IA QBDP"
        style={{
          position: 'fixed', bottom: 24, right: 24, zIndex: 900,
          width: 54, height: 54, borderRadius: '50%',
          background: 'linear-gradient(135deg,#1a56db,#1648c0)',
          border: 'none', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 4px 20px rgba(26,86,219,0.45)',
          transition: 'transform .15s',
        }}
        onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.08)'}
        onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
      >
        {open ? <ChevronDown size={22} color="white"/> : <Bot size={22} color="white"/>}
      </button>

      {/* Panel */}
      {open && (
        <div style={{
          position: 'fixed', bottom: 88, right: 24, zIndex: 900,
          width: 368, height: 500, background: 'white', borderRadius: 18,
          boxShadow: '0 12px 48px rgba(0,0,0,0.18)', display: 'flex', flexDirection: 'column',
          border: '1px solid #e5e7eb', overflow: 'hidden',
          animation: 'qbdp-pop .18s ease',
        }}>

          {/* Header */}
          <div style={{ background: 'linear-gradient(135deg,#1a56db,#1648c0)', padding: '13px 16px', display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 34, height: 34, borderRadius: '50%', background: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Sparkles size={17} color="white"/>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ color: 'white', fontWeight: 700, fontSize: 14 }}>Assistant QBDP</div>
              <div style={{ color: 'rgba(255,255,255,0.65)', fontSize: 11 }}>Données en temps réel · Groq AI</div>
            </div>
            <button onClick={() => setOpen(false)} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 8, padding: '4px 6px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
              <X size={15} color="white"/>
            </button>
          </div>

          {/* Messages */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '14px 14px 4px' }}>

            {messages.length === 0 && (
              <div style={{ textAlign: 'center', color: '#9ca3af', fontSize: 13, marginTop: 32, marginBottom: 20 }}>
                <div style={{ width: 48, height: 48, borderRadius: '50%', background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
                  <Bot size={24} color="#1a56db"/>
                </div>
                <div style={{ fontWeight: 600, color: '#374151', marginBottom: 4 }}>Bonjour !</div>
                <div style={{ fontSize: 12, lineHeight: 1.6 }}>Je connais vos employés, absences<br/>et badgeages en temps réel.</div>
              </div>
            )}

            {messages.map((m, i) => (
              <div key={i} style={{ marginBottom: 10 }}>
                <div style={{
                  maxWidth: '88%', padding: '9px 13px', borderRadius: 14, fontSize: 13, lineHeight: 1.55,
                  marginLeft: m.role === 'user' ? 'auto' : 0,
                  background: m.role === 'user' ? 'linear-gradient(135deg,#1a56db,#1648c0)' : '#f3f4f6',
                  color: m.role === 'user' ? 'white' : '#1f2937',
                  borderBottomRightRadius: m.role === 'user' ? 4 : 14,
                  borderBottomLeftRadius:  m.role === 'assistant' ? 4 : 14,
                  whiteSpace: 'pre-wrap',
                }}>
                  {m.content}
                </div>

                {m.action && !dismissed.has(i) && ACTION_LABELS[m.action.type] && (
                  <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 12, padding: '10px 13px', marginTop: 6, maxWidth: '88%' }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#1a56db', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                      {m.action.type === 'send_push' ? '🔔 Notification à envoyer' : m.action.type === 'send_email' ? '✉ Email à envoyer' : m.action.type === 'send_sms' ? '💬 SMS à envoyer' : 'Action proposée'}
                    </div>
                    <div style={{ fontSize: 12, color: '#374151', marginBottom: ACTION_PREVIEW[m.action.type] ? 6 : 8, lineHeight: 1.5, fontWeight: 600 }}>{ACTION_LABELS[m.action.type](m.action)}</div>
                    {ACTION_PREVIEW[m.action.type] && (
                      <div style={{ fontSize: 11, color: '#6b7280', background: 'white', border: '1px solid #e5e7eb', borderRadius: 8, padding: '7px 10px', marginBottom: 8, lineHeight: 1.6, whiteSpace: 'pre-wrap', maxHeight: 80, overflowY: 'auto' }}>
                        {ACTION_PREVIEW[m.action.type](m.action)}
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button onClick={() => executeAction(m.action, i)}
                        style={{ background: '#1a56db', color: 'white', border: 'none', borderRadius: 7, padding: '5px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}>
                        <Check size={12}/> Envoyer
                      </button>
                      <button onClick={() => setDismissed(prev => new Set([...prev, i]))}
                        style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 7, padding: '5px 10px', fontSize: 12, cursor: 'pointer', color: '#6b7280' }}>
                        Annuler
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}

            {loading && (
              <div style={{ display: 'flex', gap: 5, padding: '10px 13px', background: '#f3f4f6', borderRadius: 14, borderBottomLeftRadius: 4, width: 'fit-content', marginBottom: 10 }}>
                {[0, 1, 2].map(i => (
                  <div key={i} style={{ width: 7, height: 7, borderRadius: '50%', background: '#9ca3af', animation: `qbdp-bounce 1.2s ${i * 0.16}s infinite` }}/>
                ))}
              </div>
            )}
            <div ref={bottomRef}/>
          </div>

          {/* Suggestions rapides */}
          {messages.length === 0 && (
            <div style={{ padding: '0 12px 8px', display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {SUGGESTIONS.map(s => (
                <button key={s} onClick={() => send(s)}
                  style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 20, padding: '4px 11px', fontSize: 11, cursor: 'pointer', color: '#374151', transition: 'background .1s' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#eff6ff'}
                  onMouseLeave={e => e.currentTarget.style.background = '#f9fafb'}
                >{s}</button>
              ))}
            </div>
          )}

          {/* Input */}
          <div style={{ padding: '10px 12px', borderTop: '1px solid #f3f4f6', display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder={listening ? 'Parlez…' : 'Posez une question ou donnez un ordre…'}
              style={{ flex: 1, border: `1px solid ${listening ? '#fca5a5' : '#e5e7eb'}`, borderRadius: 10, padding: '8px 12px', fontSize: 13, outline: 'none', background: listening ? '#fff5f5' : '#fafafa', transition: 'all .2s' }}
            />
            {(window.SpeechRecognition || window.webkitSpeechRecognition) && (
              <button onClick={startListening} title={listening ? 'Arrêter' : 'Dicter'}
                style={{
                  background: listening ? '#ef4444' : '#f3f4f6',
                  border: 'none', borderRadius: 10, width: 38, height: 38, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  animation: listening ? 'qbdp-pulse 1.2s infinite' : 'none',
                  transition: 'background .15s',
                }}>
                {listening ? <MicOff size={15} color="white"/> : <Mic size={15} color="#6b7280"/>}
              </button>
            )}
            <button onClick={() => send()} disabled={!input.trim() || loading}
              style={{
                background: input.trim() && !loading ? 'linear-gradient(135deg,#1a56db,#1648c0)' : '#f3f4f6',
                border: 'none', borderRadius: 10, width: 38, height: 38, cursor: input.trim() && !loading ? 'pointer' : 'default',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                transition: 'background .15s',
              }}>
              <Send size={15} color={input.trim() && !loading ? 'white' : '#9ca3af'}/>
            </button>
          </div>
        </div>
      )}
    </>
  );
}
